/**
 * `F5` · `T-53` — Entrar al turno: se toca un nombre y se teclean cuatro dígitos.
 *
 * `R-09`, precisión aprobada el 2026-08-18: el PIN **abre el turno** y cada
 * sello queda firmado por esa sesión. Teclearlo en cada sello no cabe en los
 * cinco segundos y acabaría con el código en un post-it, que es peor que no
 * tener PIN.
 *
 * ── La lista dice nombres y NUNCA teléfonos ───────────────────────────────
 * Una tablet de mostrador la mira cualquiera que pase. Para tocar tu nombre en
 * una lista basta el nombre; el teléfono no pinta nada aquí y por eso el
 * contrato de `/mostrador/personal` ni lo trae.
 */
import { useState, type FormEvent, type ReactNode } from 'react'
import type { QuienPuedeEntrar, TurnoAbierto } from '@fialrest/shared'
import { mensajeParaLaPantalla } from '@fialrest/cliente'
import { abrirTurno } from './api.js'

interface Props {
  readonly personal: QuienPuedeEntrar['personal']
  readonly alEntrar: (turno: TurnoAbierto) => void
  /** `T-70` · Alguien sin PIN tocó su nombre: hay que comprobar que es ella. */
  readonly alActivarse: () => void
}

export function Turno({ personal, alEntrar, alActivarse }: Props): ReactNode {
  const [quien, setQuien] = useState<string | null>(null)
  const [pin, setPin] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [entrando, setEntrando] = useState(false)

  const elegida = personal.find((p) => p.id === quien)

  //  Recibe `quien` en vez de leerlo del estado: dentro del formulario ya está
  //  elegida —el otro camino devuelve la lista— y pedírselo al estado obligaba a
  //  un `quien === null` que no puede ocurrir. Una rama que no se puede alcanzar
  //  tampoco se puede probar, y acaba siendo un comentario que nadie comprueba.
  const entrar = async (e: FormEvent, quien: string): Promise<void> => {
    e.preventDefault()
    //  El botón ya está deshabilitado sin cuatro dígitos; esto es para quien
    //  mande el formulario por otro camino —Intro en el campo, por ejemplo.
    if (pin.length !== 4) return
    setEntrando(true)
    setError(null)
    try {
      alEntrar(await abrirTurno(quien, pin))
    } catch (falla) {
      //  El mensaje viene de la API en español y está escrito para leerse tal
      //  cual: «Ese PIN no es correcto», o el de espera con sus segundos.
      //  Inventar uno aquí sería adivinar cuál de los dos casos fue.
      setError(mensajeParaLaPantalla(falla, 'No se pudo entrar.'))
      setPin('')
    } finally {
      setEntrando(false)
    }
  }

  if (elegida === undefined) {
    return (
      <section className="caja-turno">
        <h1 className="caja-turno__titulo">¿Quién entra?</h1>
        <ul className="caja-turno__lista">
          {personal.map((p) => (
            <li key={p.id}>
              <button
                className="caja-turno__persona"
                type="button"
                //  `T-70` · Ya NO se deshabilita. Quien todavía no tiene PIN se
                //  activa una vez en su vida laboral, con SMS — y hasta hoy esta
                //  pantalla se lo decía con el botón apagado, o sea que le
                //  contaba el problema y le quitaba la salida.
                //
                //  Es la segunda empleada de toda cafetería: la que llega a una
                //  caja que ya montó su compañera. Con dos personas repartiéndose
                //  un turno sobre la misma tablet, la segunda no podía trabajar.
                onClick={() => {
                  if (!p.puedeEntrar) { alActivarse(); return }
                  setQuien(p.id); setPin(''); setError(null)
                }}
              >
                {p.nombre}
                {p.puedeEntrar ? null : <span className="caja-turno__sin-pin">activa tu PIN</span>}
              </button>
            </li>
          ))}
        </ul>
      </section>
    )
  }

  return (
    <section className="caja-turno">
      <h1 className="caja-turno__titulo">Hola, {elegida.nombre}</h1>
      <form className="caja-turno__forma" onSubmit={(e) => { void entrar(e, elegida.id) }}>
        <label className="caja-turno__etiqueta" htmlFor="pin">Tu PIN</label>
        <input
          id="pin"
          className="caja-turno__pin"
          // `inputMode` numérico y `type=password`: la tablet está de cara al
          // público y el teclado tiene que ser el de números, no el de letras.
          type="password"
          inputMode="numeric"
          autoComplete="off"
          maxLength={4}
          value={pin}
          autoFocus
          onChange={(e) => { setPin(e.target.value.replace(/\D/g, '').slice(0, 4)) }}
        />
        {error === null ? null : <p className="caja-turno__error" role="alert">{error}</p>}
        <div className="caja-turno__botones">
          <button
            className="caja-turno__volver"
            type="button"
            onClick={() => { setQuien(null); setError(null) }}
          >
            No soy yo
          </button>
          <button className="caja-turno__entrar" type="submit" disabled={pin.length !== 4 || entrando}>
            {entrando ? 'Entrando…' : 'Entrar'}
          </button>
        </div>
      </form>
    </section>
  )
}
